class VariantAvailabilityHandler {
  constructor() {
    this.product = null;
    this.variants = [];
    this.currentColor = null;
    this.initialize();
  }

  initialize() {
    try {
      // First try to get variants with inventory data from window.theme
      if (window.theme?.available_variants) {
        this.variants = window.theme.available_variants;
      } else if (window.theme?.product?.variants) {
        this.variants = window.theme.product.variants;
      } else if (window.ShopifyAnalytics?.meta?.product) {
        console.log("Found product data from ShopifyAnalytics");
        this.product = window.ShopifyAnalytics.meta.product;
        this.variants = this.product.variants;

        // Try to find inventory data from any script tags
        const scriptTags = Array.from(
          document.querySelectorAll('script[type="application/json"]')
        );
        for (const script of scriptTags) {
          try {
            const data = JSON.parse(script.textContent);
            if (data.inventory || data.variants || data.product) {
              console.log("Found additional product data:", data);
              this.additionalProductData = data;
              break;
            }
          } catch (e) {
            // Skip invalid JSON
          }
        }
      }

      if (this.variants.length > 0) {
        // Add our custom styles for unavailable variants
        this.addCustomStyles();

        const colors = [
          ...new Set(this.variants.map((v) => v.public_title.split(" / ")[0])),
        ];
        console.log("Available colors from variants:", colors);

        this.setupEventListeners();
        this.initializeCurrentColor();
      }
    } catch (error) {
      console.error("Error initializing variant handler:", error);
    }
  }

  addCustomStyles() {
    const styleId = "variant-availability-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        /* Target span next to disabled input */
        .options-selection__option-value-input:disabled + .options-selection__option-value-name {
          cursor: default;
          background-image: url(data:image/svg+xml,<svg width="44" height="44" viewBox="0 0 100 100" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"><line x1="0" y1="100" x2="100" y2="0" stroke-width="5" stroke="%23282c30"/></svg>);
          background-size: 100% 100%;
          outline: none;
          opacity: 0.5;
        }

        /* Additional styles for unavailable/soldout states */
        .options-selection__option-value-label.unavailable.soldout {
          pointer-events: none !important;
          opacity: 0.7 !important;
        }

        .options-selection__option-value-name.unavailable.soldout {
          pointer-events: none !important;
          opacity: 0.7 !important;
        }
      `;
      document.head.appendChild(style);
      console.log("Added strikethrough styles");
    }
  }

  initializeCurrentColor() {
    // First try to get the selected color from the actual variant selection
    const selectedVariantTitle = this.getSelectedVariantTitle();
    if (selectedVariantTitle) {
      const color = selectedVariantTitle.split(" / ")[0];
      this.setCurrentColor(color);
      this.updateSizeAvailability();
      return;
    }

    // Fallback to first variant's color
    if (this.variants.length > 0) {
      const firstColor = this.variants[0].public_title.split(" / ")[0];
      this.setCurrentColor(firstColor);
      this.updateSizeAvailability();
    }
  }

  getSelectedVariantTitle() {
    // Try to get the selected variant from the URL or form
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");

    if (variantId) {
      const variant = this.variants.find((v) => v.id.toString() === variantId);
      if (variant) return variant.public_title;
    }

    // Try to get from form
    const variantSelect = document.querySelector('select[name="id"]');
    if (variantSelect) {
      const variant = this.variants.find(
        (v) => v.id.toString() === variantSelect.value
      );
      if (variant) return variant.public_title;
    }

    return null;
  }

  setupEventListeners() {
    // Listen for color swatch clicks
    const colorSwatches = document.querySelectorAll(
      ".options-selection__option-swatch-inner, [data-option-index='0'] input[type='radio']"
    );
    console.log("Found color swatches:", colorSwatches.length);

    // Add click listeners to color swatches
    colorSwatches.forEach((swatch) => {
      swatch.addEventListener("click", (e) => {
        console.log("Color swatch clicked:", e.target);

        // Give the DOM a moment to update
        setTimeout(() => {
          const selectedVariantTitle = this.getSelectedVariantTitle();
          if (selectedVariantTitle) {
            const color = selectedVariantTitle.split(" / ")[0];
            console.log("Color change detected:", color);
            this.setCurrentColor(color);
            this.updateSizeAvailability();
          }
        }, 100);
      });
    });

    // Listen for select/input changes
    document.addEventListener("change", (e) => {
      if (
        e.target.matches(
          'select[data-option="Color"], input[data-option="Color"]'
        )
      ) {
        const color = e.target.value;
        console.log("Color input changed to:", color);
        this.setCurrentColor(color);
        this.updateSizeAvailability();
      }
    });

    // Listen for variant change events
    document.addEventListener("variant:change", (e) =>
      this.handleVariantChange(e)
    );
    document.addEventListener("variant_change", (e) =>
      this.handleVariantChange(e)
    );
    document.addEventListener("variantChange", (e) =>
      this.handleVariantChange(e)
    );
  }

  handleVariantChange(e) {
    console.log("Variant change event:", e);
    const variantTitle =
      e.detail?.variant?.title || this.getSelectedVariantTitle();
    if (variantTitle) {
      const color = variantTitle.split(" / ")[0];
      console.log("Variant changed, new color:", color);
      this.setCurrentColor(color);
      this.updateSizeAvailability();
    }
  }

  setCurrentColor(color) {
    this.currentColor = color;
    console.log("Color set to:", color);
  }

  getVariantsByCurrentColor() {
    if (!this.currentColor) return this.variants;

    const filteredVariants = this.variants.filter(
      (variant) => variant.public_title.split(" / ")[0] === this.currentColor
    );

    console.log(
      "Filtered variants for color:",
      this.currentColor,
      filteredVariants
    );
    return filteredVariants;
  }

  isSizeAvailableForCurrentColor(size) {
    if (!size) return true;

    const colorVariants = this.getVariantsByCurrentColor();
    const sizeVariant = colorVariants.find(
      (variant) => variant.public_title.split(" / ")[1] === size
    );

    // If we can't find the variant at all, it's not available
    if (!sizeVariant) {
      console.log(
        `Size ${size} not found in variants for current color - marking as unavailable`
      );
      return false;
    }

    // Check inventory data from additionalProductData first
    if (this.additionalProductData?.variantInventory) {
      const variantId = sizeVariant.id.toString();
      const inventoryData =
        this.additionalProductData.variantInventory[variantId];

      console.log(
        "Checking availability for size:",
        size,
        "Variant ID:",
        variantId,
        "Inventory data:",
        inventoryData
      );

      if (inventoryData) {
        const isAvailable = inventoryData.inventory_quantity > 0;
        console.log(
          `Size ${size} availability based on inventory: ${isAvailable}`
        );
        return isAvailable;
      }
    }

    // If no inventory data, check if variant is marked as available
    if (sizeVariant.available !== undefined) {
      console.log(
        `Size ${size} availability based on variant.available: ${sizeVariant.available}`
      );
      return sizeVariant.available;
    }

    // Default to checking variant's inventory quantity if available
    if (sizeVariant.inventory_quantity !== undefined) {
      const isAvailable = sizeVariant.inventory_quantity > 0;
      console.log(
        `Size ${size} availability based on inventory_quantity: ${isAvailable}`
      );
      return isAvailable;
    }

    console.log(
      `No availability data found for size ${size} - defaulting to unavailable`
    );
    return false;
  }

  updateSizeAvailability() {
    // Query all size-related elements
    const sizeElements = document.querySelectorAll(`
      .options-selection__option-value-name,
      [data-option="Size"] label,
      .swatch-element[data-value],
      .swatch input[type="radio"][value]
    `);

    console.log("Found size elements:", sizeElements.length);

    sizeElements.forEach((element) => {
      // Get size value from various possible sources
      const size =
        element.getAttribute("data-value") ||
        element.getAttribute("value") ||
        element.textContent.trim();

      const isAvailable = this.isSizeAvailableForCurrentColor(size);

      // Apply unavailable/soldout classes to the element and its parent wrapper
      const elements = [
        element,
        element.closest(".swatch-element"),
        element.closest("label"),
        element.closest(".options-selection__option"),
      ].filter(Boolean);

      elements.forEach((el) => {
        if (!isAvailable) {
          el.classList.add("unavailable", "soldout");

          // Find and disable the input
          const input =
            el.querySelector(".options-selection__option-value-input") ||
            el
              .closest("label")
              ?.querySelector(".options-selection__option-value-input");
          if (input) {
            input.disabled = true;
            input.setAttribute("data-variant-option-available", "false");
          }

          // Add classes to both label and span
          const label = el.closest("label") || el.querySelector("label");
          const span =
            el.querySelector(".options-selection__option-value-name") ||
            el
              .closest("label")
              ?.querySelector(".options-selection__option-value-name");

          if (label) label.classList.add("unavailable", "soldout");
          if (span) span.classList.add("unavailable", "soldout");
        } else {
          el.classList.remove("unavailable", "soldout");

          // Find and enable the input
          const input =
            el.querySelector(".options-selection__option-value-input") ||
            el
              .closest("label")
              ?.querySelector(".options-selection__option-value-input");
          if (input) {
            input.disabled = false;
            input.setAttribute("data-variant-option-available", "true");
          }

          // Remove classes from both label and span
          const label = el.closest("label") || el.querySelector("label");
          const span =
            el.querySelector(".options-selection__option-value-name") ||
            el
              .closest("label")
              ?.querySelector(".options-selection__option-value-name");

          if (label) label.classList.remove("unavailable", "soldout");
          if (span) span.classList.remove("unavailable", "soldout");
        }
      });

      console.log(`Size ${size}: ${isAvailable ? "available" : "unavailable"}`);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    () => new VariantAvailabilityHandler()
  );
} else {
  new VariantAvailabilityHandler();
}
